import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { FlaskConical, Clock3, X, ImagePlus, Loader2 } from "lucide-react";
import { fmtLocalDT as fmtDT } from "@/lib/clinical";
import { useAuth } from "@/lib/auth";
import InvestigationReports, { RowActions } from "./InvestigationReports";

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const CATEGORIES = ["CBC", "CRP", "ABG", "Electrolytes", "LFT", "RFT", "Blood Culture", "Urine Culture", "Bilirubin", "Serum Bilirubin", "2D Echocardiography", "X-ray", "USG", "CT", "MRI", "Other"];

const IMAGE_CATEGORIES = new Set(["X-ray", "USG", "CT", "MRI", "2D Echocardiography"]);

// Simple reference ranges for neonate flags
const REF = {
  CBC:          { Hb: [14, 22], WBC: [5, 30], Platelets: [150, 450] },
  CRP:          { CRP: [0, 6] },
  ABG:          { pH: [7.30, 7.45], pCO2: [35, 50], pO2: [50, 90], HCO3: [18, 26], BE: [-5, 5], Lactate: [0, 2] },
  Electrolytes: { Na: [135, 145], K: [3.5, 5.5], Cl: [98, 108], Ca: [8, 10.5] },
  Bilirubin:    { Total: [0, 12], Direct: [0, 1.5] },
  "Serum Bilirubin": { Total: [0, 12], Direct: [0, 1.5], Indirect: [0, 10] },
};
function flag(cat, key, val) {
  const r = REF[cat]?.[key]; if (!r || val === "" || val == null) return "";
  const n = Number(val); if (isNaN(n)) return "";
  if (n > r[1]) return "abnormal-high";
  if (n < r[0]) return "abnormal-low";
  return "";
}

function fmtDTLocal(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16).replace("T", " "); }
}
// unused (kept for jsdoc/back-compat) but consolidated shared helper:

export default function InvestigationsTab({ patient }) {
  const [category, setCategory] = useState("CBC");
  const [rows, setRows] = useState([]);
  const [values, setValues] = useState({});
  const [pendingId, setPendingId] = useState(null);   // if set, save updates that pending record
  const [editId, setEditId] = useState(null);
  const { user } = useAuth();
  const canManage = user?.role === "doctor" || user?.role === "admin";
  const [entryAt, setEntryAt] = useState("");        // datetime-local; blank = now
  const [pendingFileIds, setPendingFileIds] = useState([]);   // file_ids uploaded for the row being saved
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(
    () => api.get(`/investigations/${patient.id}`).then(({ data }) => setRows(data)).catch(() => {}),
    [patient.id]
  );
  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/files/upload?patient_id=${patient.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.id);
      }
      setPendingFileIds((s) => [...s, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached — save to persist`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pending = useMemo(() => rows.filter((r) => r.pending), [rows]);
  const done = useMemo(() => rows.filter((r) => !r.pending), [rows]);
  const filtered = done.filter((r) => r.category === category);
  const keys = Object.keys(REF[category] || {});

  const save = async () => {
    const at = entryAt ? new Date(entryAt).toISOString() : null;
    const payload = { patient_id: patient.id, category, values };
    if (at) payload.at = at;
    if (pendingFileIds.length) payload.file_ids = pendingFileIds;
    try {
      if (editId) {
        await api.patch(`/investigations/${editId}`, { values, at: at || undefined, file_ids: pendingFileIds });
        toast.success("Investigation updated");
      } else if (pendingId) {
        const patch = { values, pending: false };
        if (at) patch.at = at;
        if (pendingFileIds.length) patch.file_ids = pendingFileIds;
        await api.patch(`/investigations/${pendingId}`, patch);
        toast.success("Pending investigation completed");
      } else {
        await api.post("/investigations", payload);
        toast.success("Recorded");
      }
      setValues({});
      setPendingId(null);
      setEditId(null);
      setEntryAt("");
      setPendingFileIds([]);
      load();
    } catch { toast.error("Save failed"); }
  };

  const openPending = (p) => {
    setCategory(p.category);
    setPendingId(p.id);
    setEditId(null);
    setValues({});
  };
  const startEdit = (r) => {
    setCategory(r.category);
    setPendingId(null);
    setEditId(r.id);
    setValues({ ...(r.values || {}) });
    setEntryAt(isoToLocal(r.at));
    setPendingFileIds(r.file_ids || []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditId(null); setValues({}); setEntryAt(""); setPendingFileIds([]); };
  const removeEntry = async (r) => {
    if (!window.confirm(`Delete this ${r.category} entry from ${fmtDT(r.at)}? This cannot be undone.`)) return;
    try {
      await api.delete(`/investigations/${r.id}`);
      toast.success("Investigation deleted");
      if (editId === r.id) cancelEdit();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const cancelPending = async (p) => {
    if (!window.confirm(`Cancel pending ${p.category}?`)) return;
    try { await api.patch(`/investigations/${p.id}`, { pending: false, values: { cancelled: true } }); toast.success("Pending cancelled"); load(); }
    catch { toast.error("Failed"); }
  };

  const trendKey = category === "Bilirubin" ? "Total" : category === "CRP" ? "CRP" : keys[0];
  const chart = trendKey ? filtered.slice().reverse().map((r) => ({ t: (r.at || "").slice(5, 10), v: Number(r.values?.[trendKey]) })) : [];

  return (
    <div className="space-y-6" data-testid="investigations-tab">
      {/* Pending advised investigations */}
      {pending.length > 0 && (
        <div className="card-clinical p-5 border-l-4 border-l-amber-500" data-testid="pending-investigations">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock3 size={14} className="text-amber-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">
                Pending investigations ({pending.length}) — advised on rounds, result not yet in
              </span>
            </div>
          </div>
          <ul className="space-y-2">
            {pending.map((p) => (
              <li
                key={p.id}
                className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${
                  pendingId === p.id ? "border-primary bg-primary/5" : "border-border"
                }`}
                data-testid={`pending-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
                    <FlaskConical size={9} className="mr-1" /> {p.category}
                  </span>
                  <span className="text-xs text-muted-foreground tabular">advised {fmtDT(p.at)}</span>
                  {p.by && <span className="text-[11px] text-muted-foreground">by {p.by}</span>}
                  {p.remarks && <span className="text-xs italic truncate">"{p.remarks}"</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm" variant="outline"
                    className="rounded-full h-7 text-xs"
                    onClick={() => openPending(p)}
                    data-testid={`pending-fill-${p.id}`}
                  >Fill result</Button>
                  <Button
                    size="sm" variant="ghost"
                    className="rounded-full h-7 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => cancelPending(p)}
                    title="Cancel this pending request"
                  ><X size={12} /></Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Select value={category} onValueChange={(v) => { setCategory(v); setPendingId(null); if (editId) cancelEdit(); }}>
          <SelectTrigger className="w-56" data-testid="inv-category"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        {pendingId && (
          <div className="text-xs text-primary flex items-center gap-2">
            Filling pending {category}
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground text-[11px] underline underline-offset-2"
              onClick={() => setPendingId(null)}
            >switch to new entry</button>
          </div>
        )}
        {editId && (
          <div className="text-xs text-primary flex items-center gap-2" data-testid="inv-editing-banner">
            Editing {category} entry
            <button type="button" className="text-muted-foreground hover:text-foreground text-[11px] underline underline-offset-2"
                    onClick={cancelEdit} data-testid="inv-cancel-edit">cancel edit</button>
          </div>
        )}
      </div>

      <div className={`card-clinical p-4 ${pendingId || editId ? "border-primary/40" : ""}`}>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
          {editId ? `Edit ${category} entry` : pendingId ? `Fill result for pending ${category}` : `New ${category} entry`}
        </div>
        <div className="mb-3 max-w-xs">
          <label className="text-[11px] text-muted-foreground">Date &amp; time of report</label>
          <Input type="datetime-local" value={entryAt} onChange={(e) => setEntryAt(e.target.value)}
                 data-testid="inv-entry-at"
                 placeholder="leave blank for now" />
          <div className="text-[10px] text-muted-foreground mt-1">Blank = current time. Backdate to log reports missed on earlier days.</div>
        </div>
        {IMAGE_CATEGORIES.has(category) && (
          <div className="mb-3">
            <label className="text-[11px] text-muted-foreground">Attach image / PDF (X-ray, USG, echo…)</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file" accept="image/*,application/pdf" multiple
                onChange={(e) => uploadFiles(e.target.files)}
                className="hidden" data-testid="inv-file-input"
              />
              <Button type="button" size="sm" variant="outline" className="rounded-full"
                      onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      data-testid="inv-file-btn">
                {uploading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <ImagePlus size={12} className="mr-1" />}
                {uploading ? "Uploading…" : "Attach files"}
              </Button>
              {pendingFileIds.map((id) => (
                <FileThumb key={id} id={id} onRemove={() => setPendingFileIds((s) => s.filter((x) => x !== id))} />
              ))}
            </div>
          </div>
        )}
        {keys.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {keys.map((k) => (
              <div key={k} className="space-y-1">
                <label className="text-[11px] text-muted-foreground">{k}</label>
                <Input type="number" step="0.01" value={values[k] || ""} onChange={(e) => setValues((s) => ({ ...s, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
        ) : (
          <textarea
            className="w-full min-h-[72px] rounded-md border border-input bg-background p-2 text-sm"
            placeholder="Findings / report text (paste report)"
            value={values.report || ""}
            onChange={(e) => setValues({ report: e.target.value })}
          />
        )}
        <div className="flex justify-end mt-4">
          <Button onClick={save} className="rounded-full" data-testid="inv-record">
            {editId ? "Update" : pendingId ? "Complete pending" : "Record"}
          </Button>
        </div>
      </div>

      {trendKey && chart.filter((x) => !isNaN(x.v)).length > 1 && (
        <div className="card-clinical p-4">
          <div className="text-sm font-medium mb-2">{category} — {trendKey} trend</div>
          <div className="h-40">
            <ResponsiveContainer><LineChart data={chart}>
              <XAxis dataKey="t" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="v" stroke="#0284C7" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart></ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card-clinical overflow-auto">
        <table className="vitals-grid w-full tabular">
          <thead>
            <tr>
              <th>Time</th>
              {keys.length > 0 ? keys.map((k) => <th key={k}>{k}</th>) : <th>Report</th>}
              <th>By</th>
              {canManage && <th />}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={keys.length + 3 || 4} className="text-center text-muted-foreground py-6">No records</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className={editId === r.id ? "bg-primary/5" : ""}>
                <td className="whitespace-nowrap">{fmtDT(r.at)}</td>
                {keys.length > 0
                  ? keys.map((k) => <td key={k} className={flag(category, k, r.values?.[k])}>{r.values?.[k] ?? ""}</td>)
                  : <td className="whitespace-pre-wrap">
                      {r.values?.report}
                      {(r.file_ids || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {r.file_ids.map((id) => <FileThumb key={id} id={id} />)}
                        </div>
                      )}
                    </td>}
                <td className="text-muted-foreground text-xs">{r.by}</td>
                {canManage && <td><RowActions row={r} onEdit={startEdit} onDelete={removeEntry} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InvestigationReports rows={done} canManage={canManage} onEdit={startEdit} onDelete={removeEntry}
        renderFiles={(ids) => <div className="mt-2 flex flex-wrap gap-2">{ids.map((id) => <FileThumb key={id} id={id} />)}</div>} />
    </div>
  );
}

function FileThumb({ id, onRemove }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get(`/files/${id}/meta`).then(({ data }) => setMeta(data)).catch(() => setMeta({ error: true }));
  }, [id]);
  const isImage = meta?.mime?.startsWith("image/");
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`;
  const auth = localStorage.getItem("nicu.token");
  return (
    <div className="relative group inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs" data-testid={`file-thumb-${id}`}>
      {isImage ? (
        <img alt="" className="h-10 w-10 rounded object-cover"
             src={`${src}?t=${auth ? "" : ""}`}
             onError={(e) => { e.target.style.display = "none"; }} />
      ) : (
        <span className="h-10 w-10 rounded bg-muted grid place-items-center text-[10px]">PDF</span>
      )}
      <a href={src} target="_blank" rel="noreferrer" className="max-w-[140px] truncate hover:underline">
        {meta?.filename || id.slice(0, 8)}
      </a>
      {onRemove && (
        <button type="button" onClick={onRemove}
                className="ml-1 text-muted-foreground hover:text-destructive"
                title="Remove">
          <X size={11} />
        </button>
      )}
    </div>
  );
}
